import React, { useRef, useState, useCallback, createRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import theme from '../../../style/theme'
import ResponsiveImage from '../../ResponsiveImage'
import { sliceMarginStyles, span } from '../../../style/span'
import SplitWordsAnimation from '../../SplitWordsAnimation'
import { vw } from '../../../style/vw'
import cn from 'classnames'
import CircularProgress from './CircularProgress'
import RichText from '../../RichText'
import Button from '../../Button'
import gsap from 'gsap'
import LogoTicker from './LogoTicker'
import isEmpty from 'lodash/isEmpty'

export default function HomepageHeroV2Slice ({ slice }) {
  const [index, setIndex] = useState(0)
  const localsRef = useRef({ index: 0 })
  const imagesRef = useRef()
  const titlesRef = useRef()
  const footerRef = useRef()
  const titlesArrayRef = useRef([])
  titlesArrayRef.current = slice.slides.map((_, i) => titlesArrayRef.current[i] ?? createRef())
  const classes = useStyles()

  const onTimeout = useCallback((newIndex) => {
    if (localsRef.current.tl) localsRef.current.tl.kill()
    if (!imagesRef.current) return null
    const currentIndex = localsRef.current.index
    const nextIndex = newIndex === undefined ? (localsRef.current.index + 1) % slice.slides.length : newIndex
    var tl = gsap.timeline()

    tl.set(imagesRef.current.children, { opacity: 0, zIndex: 0 })
    tl.set(imagesRef.current.children[currentIndex], { opacity: 1, zIndex: 2 })
    tl.set(imagesRef.current.children[nextIndex], { opacity: 1, zIndex: 1 })
    tl.to(imagesRef.current.children[currentIndex], { opacity: 0, duration: 0.3, ease: 'power2.inOut' })

    tl.to(titlesRef.current.children[currentIndex], { opacity: 0, duration: 0.3, ease: 'power2.inOut' }, 0)
    tl.set(titlesRef.current.children[nextIndex], { opacity: 1 }, 0)
    titlesArrayRef.current[nextIndex].animateIn()
    titlesArrayRef.current[currentIndex].animateOut()

    localsRef.current.index = nextIndex
    localsRef.current.tl = tl
    setIndex(nextIndex)
  }, [slice.slides.length])

  useEffect(() => {
    if (!footerRef.current) return
    gsap.to(footerRef.current.children, { opacity: 1, y: 0, stagger: 0.2, duration: 1.2, ease: 'power2.inOut' })
  }, [])

  return (
    <>
      <section className={cn(classes.container, isEmpty(slice.logoTicker) && classes.containerWithMargin)}>
        <div ref={imagesRef}>
          {slice.slides.map((slide, i) => (
            <ResponsiveImage key={i} className={cn(classes.image, index === i && 'show')} {...slide.image} disableLazy parallax={theme.parallax} />
          ))}
        </div>
        <div className={classes.content}>
          <div className={classes.titleWrapper} ref={titlesRef}>
            {slice.slides.map((slide, i) => (
              <SplitWordsAnimation key={i} className={classes.splitTitleContainer} ref={el => { titlesArrayRef.current[i] = el }} animateOnMount={i === 0}>
                <h1 className={classes.title}>
                  {slide?.title}
                </h1>
              </SplitWordsAnimation>
            ))}
          </div>
          <div className={classes.footer} ref={footerRef}>
            <div className={classes.paginatorContainer}>
              {slice.slides.map((slide, i) => (
                <div key={i} className={cn(classes.paginator, { active: index === i })} onClick={() => onTimeout(i)}>
                  <div><CircularProgress className={cn(classes.progress, index !== i && 'hide')} start={index === i} onComplete={onTimeout} /></div>
                  <div>{slide.tagLine}</div>
                </div>
              ))}
            </div>
            <div className={classes.copyContainer}>
              <RichText content={slice.copy?.text} className={classes.copy} />
              <Button link={slice.cta?.[0]} size='sm' uppercase={false} theme='white' />
            </div>
          </div>
        </div>
      </section>
      <LogoTicker label={slice.logoTickerLabel} items={slice.logoTicker} />
    </>
  )
}

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    display: 'flex',
    minHeight: '100vh',
    color: theme.colors.white,
    [theme.breakpoints.up('md')]: {
    }
  },
  containerWithMargin: {
    extend: [sliceMarginStyles]
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0,
    transition: 'opacity 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4 )',
      zIndex: 1,
      pointerEvents: 'none'
    },
    '&.show': {
      opacity: 1
    }
  },
  content: {
    zIndex: 10,
    position: 'relative',
    width: '100%',
    minHeight: '100%',
    margin: span(1),
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      margin: span(1, 'md')
    }
  },
  titleWrapper: {
    position: 'relative',
    minHeight: '4em',
    fontSize: vw(48),
    lineHeight: 1,
    marginTop: '25vh',
    flexGrow: 0,
    flexShrink: 0,
    [theme.breakpoints.up('md')]: {
      flexGrow: 1,
      flexShrink: 0,
      marginTop: 0,
      fontSize: vw(100, 'desktop'),
      width: span(16, 'md')
    }
  },
  splitTitleContainer: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)'
  },
  title: {
    fontSize: vw(48),
    lineHeight: 1,
    fontWeight: theme.fonts.headingsFontWeight,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(100, 'desktop')
    }
  },
  footer: {
    flexGrow: 1,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'space-around',
    [theme.breakpoints.up('md')]: {
      flexGrow: 0,
      flexDirection: 'row',
      height: 'auto',
      justifyContent: 'flex-start'
    },
    '& > *': {
      opacity: 0,
      transform: 'translateY(20px)'
    }
  },
  copyContainer: {
    fontSize: vw(16),
    marginBottom: vw(32),
    [theme.breakpoints.up('md')]: {
      gap: vw(8, 'desktop'),
      fontSize: vw(18, 'desktop'),
      width: span(9, 'md'),
      marginBottom: 0
    }
  },
  copy: {
    marginBottom: vw(16),
    [theme.breakpoints.up('md')]: {
      marginBottom: vw(16, 'desktop')
    }
  },
  paginatorContainer: {
    [theme.breakpoints.up('md')]: {
      minWidth: span(6, 'md')
    }
  },
  paginator: {
    cursor: 'pointer',
    display: 'flex',
    gap: vw(8),
    opacity: 0.4,
    fontSize: vw(13),
    transition: 'opacity 0.2s ease',
    [theme.breakpoints.up('md')]: {
      gap: vw(8, 'desktop'),
      fontSize: vw(13, 'desktop')
    },
    '&.active': {
      opacity: 1
    },

    '& > div': {
      fontFamily: theme.fonts.headings
    }
  },
  progress: {
    width: vw(10),
    height: vw(10),
    [theme.breakpoints.up('md')]: {
      width: vw(10, 'desktop'),
      height: vw(10, 'desktop')
    },
    '&.hide': {
      visibility: 'hidden'
    }
  }
})
